/**
 * This utility file provides functions to process and transform raw materialized table data into a structured format
 * suitable for use in the table definition editor. It handles:
 * - Mapping raw table rows and headers to processed cells with additional metadata (e.g., overrides, evaluation
 * details).
 * - Filtering out unnecessary property and unit-of-measure (UOM) columns from the processed data.
 * - Processing individual cell types (e.g., numeric, UUID, boolean) based on their column definitions.
 * - Adding evaluation details and override indicators to cells when applicable.
 */
import _ from 'lodash';
import { sqScalingTableDefinitionStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import {
  CSV_TYPES,
  PROPERTY_COLUMN_MATCH_SEGMENT,
  UOM_COLUMN_MATCH_SUFFIX,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import { toNumber } from '@/utilities/numberHelper.utilities';
import type {
  BooleanTableCell,
  ColumnDefinition,
  EditorType,
  ItemTableCell,
  MaterializedTableCell,
  MaterializedTableHeader,
  MaterializedTableOutput,
  MaterializedTableRow,
  NumericTableCell,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  TableJoinData,
  TextTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';

/** Fast lookup for header name -> column index. */
type HeaderIndexMap = Map<string, number>;
/** Fast lookup for column name -> column definition. */
type ColumnByNameMap = Map<string, ColumnDefinition>;
/** Fast lookup for column id -> override column index. */
type OverrideIndexMap = Map<string, number>;
/** Property name plus optional header index for UUID property columns. */
type PropertyHeaderIndex = { property: string; index?: number };
/** Fast lookup for UUID column name -> property header indices. */
type PropertyIndexMap = Map<string, PropertyHeaderIndex[]>;
/** Fast lookup for UUID column name -> alias metadata header indices. */
type AliasHeaderIndexMap = Map<string, number[]>;
/** Rule results from evaluation details. */
type EvaluationRuleResults = NonNullable<ColumnDefinition['evaluationDetails']>[number]['ruleResults'];
/** Lookup maps for evaluation details by item and datum ids. */
type EvaluationLookup = {
  byItemAndDatum: Map<string, EvaluationRuleResults>;
  byItemFirst: Map<string, EvaluationRuleResults>;
};
/** Fast lookup for column name -> evaluation details lookup. */
type EvaluationLookupMap = Map<string, EvaluationLookup>;

/** Processing instructions aligned to each header index. */
type HeaderProcessingPlan = {
  header: MaterializedTableHeader;
  column?: ColumnDefinition;
  overrideIndex?: number;
  evaluationLookup?: EvaluationLookup;
  uomIndex?: number;
  propertyIndices?: PropertyHeaderIndex[];
  aliasHeaderIndices?: number[];
};

/** Shared lookup context used to process every row. */
type ProcessingContext = {
  processingPlans: HeaderProcessingPlan[];
  itemIdIndex: number;
  datumIdIndex: number;
};

const getHeaderName = (header: MaterializedTableHeader) => header.name ?? '';

/** Build a name -> index map for headers. */
const buildHeaderIndexMap = (headers: MaterializedTableHeader[]): HeaderIndexMap => {
  return headers.reduce((map, header, index) => {
    const headerName = getHeaderName(header);
    if (!headerName) {
      return map;
    }
    map.set(headerName, index);
    return map;
  }, new Map<string, number>());
};

/** Build a columnName -> columnDefinition map for O(1) access. */
const buildColumnByNameMap = (columns: ColumnDefinition[]): ColumnByNameMap => {
  return columns.reduce((map, column) => {
    map.set(column.columnName, column);
    return map;
  }, new Map<string, ColumnDefinition>());
};

/** Capture override header indices keyed by column id. */
const buildOverrideIndexMap = (headers: MaterializedTableHeader[]): OverrideIndexMap => {
  const overrideSuffixMatch = /\."([^"]+)_overrides"$/;
  return headers.reduce((map, header, index) => {
    const headerName = getHeaderName(header);
    if (!headerName) {
      return map;
    }
    const match = headerName.match(overrideSuffixMatch);
    if (match?.[1]) {
      const columnId = match[1].toLowerCase();
      if (!map.has(columnId)) {
        map.set(columnId, index);
      }
    }
    return map;
  }, new Map<string, number>());
};

/** Precompute property header indices for UUID columns. */
const buildPropertyIndexMap = (columns: ColumnDefinition[], headerIndexByName: HeaderIndexMap): PropertyIndexMap => {
  return columns.reduce((map, column) => {
    if (column.columnType !== ColumnTypeEnum.UUID) {
      return map;
    }
    const propertySet = new Set<string>([
      ...(column.additionalProperties || []),
      SeeqNames.Properties.IsModifiedOutsideTable,
      SeeqNames.Properties.Type,
      SeeqNames.Properties.Name,
      ...(column.displayProperty ? [column.displayProperty] : []),
    ]);
    const propertyIndices = Array.from(propertySet).map((property) => ({
      property,
      index: headerIndexByName.get(`${column.columnName}${PROPERTY_COLUMN_MATCH_SEGMENT}${property}`),
    }));
    map.set(column.columnName, propertyIndices);
    return map;
  }, new Map<string, PropertyHeaderIndex[]>());
};

/** Precompute alias metadata header indices grouped by base column name. */
const buildAliasHeaderIndexMap = (
  headers: MaterializedTableHeader[],
  headerIndexByName: HeaderIndexMap,
  tableJoinData?: TableJoinData[],
): AliasHeaderIndexMap => {
  if (!tableJoinData?.length) {
    return new Map<string, number[]>();
  }

  const joinAliasByTableId = tableJoinData.reduce((map, join) => {
    const tableId = join.tableJoinInput.tableDefinitionId.toUpperCase();
    if (!join.aliasColumnName) {
      return map;
    }
    const aliasSet = map.get(tableId) ?? new Set<string>();
    aliasSet.add(join.aliasColumnName);
    map.set(tableId, aliasSet);
    return map;
  }, new Map<string, Set<string>>());

  const metadataHeaders = headers.filter((header) =>
    header.name?.includes(SeeqNames.MaterializedTables.MaterializedTableJoinSuffix),
  );
  const metadataTableColumns = extractMetadataHeaders(metadataHeaders);

  return metadataTableColumns.reduce((map, metadataColumn) => {
    const aliasSet = joinAliasByTableId.get(metadataColumn.sourceTableId);
    if (!aliasSet?.has(metadataColumn.metadataName)) {
      return map;
    }
    const metadataHeaderName = getHeaderName(metadataColumn.header);
    if (!metadataHeaderName) {
      return map;
    }
    const headerIndex = headerIndexByName.get(metadataHeaderName);
    if (headerIndex === undefined) {
      return map;
    }
    const indices = map.get(metadataColumn.columnName) ?? [];
    indices.push(headerIndex);
    map.set(metadataColumn.columnName, indices);
    return map;
  }, new Map<string, number[]>());
};

/** Build fast evaluation lookup maps for columns that define evaluation details. */
const buildEvaluationLookupMap = (columns: ColumnDefinition[]): EvaluationLookupMap => {
  return columns.reduce((map, column) => {
    if (!column.evaluationDetails?.length) {
      return map;
    }
    const lookup: EvaluationLookup = {
      byItemAndDatum: new Map<string, EvaluationRuleResults>(),
      byItemFirst: new Map<string, EvaluationRuleResults>(),
    };
    column.evaluationDetails.forEach((detail) => {
      const itemIdKey = detail.itemId.toUpperCase();
      const datumIdKey = detail.datumId?.toUpperCase() ?? '';
      lookup.byItemAndDatum.set(`${itemIdKey}|${datumIdKey}`, detail.ruleResults);
      if (!lookup.byItemFirst.has(itemIdKey)) {
        lookup.byItemFirst.set(itemIdKey, detail.ruleResults);
      }
    });
    map.set(column.columnName, lookup);
    return map;
  }, new Map<string, EvaluationLookup>());
};

/** Build per-header processing instructions to avoid repeated lookups. */
const buildHeaderProcessingPlans = (
  headers: MaterializedTableHeader[],
  columnsByName: ColumnByNameMap,
  overrideIndexByColumnId: OverrideIndexMap,
  headerIndexByName: HeaderIndexMap,
  propertyIndexByColumnName: PropertyIndexMap,
  aliasHeaderIndexByColumnName: AliasHeaderIndexMap,
  evaluationLookupByColumnName: EvaluationLookupMap,
): HeaderProcessingPlan[] => {
  return headers.map((header) => {
    const headerName = getHeaderName(header);
    const column = headerName ? columnsByName.get(headerName) : undefined;
    const overrideIndex = column?.id ? overrideIndexByColumnId.get(column.id.toLowerCase()) : undefined;
    const evaluationLookup = column ? evaluationLookupByColumnName.get(column.columnName) : undefined;
    const plan: HeaderProcessingPlan = {
      header,
      column,
      overrideIndex,
      evaluationLookup,
    };

    if (header.type === ColumnTypeEnum.NUMERIC) {
      plan.uomIndex = headerName ? headerIndexByName.get(`${headerName}${UOM_COLUMN_MATCH_SUFFIX}`) : undefined;
    }

    if (header.type === ColumnTypeEnum.UUID) {
      plan.propertyIndices = headerName ? (propertyIndexByColumnName.get(headerName) ?? []) : [];
      plan.aliasHeaderIndices = headerName ? aliasHeaderIndexByColumnName.get(headerName) : undefined;
    }

    return plan;
  });
};

/** Return first non-nullish row value for the provided indices. */
const getFirstDefinedValue = (row: MaterializedTableRow, indices?: number[]) => {
  if (!indices?.length) {
    return undefined;
  }
  for (const index of indices) {
    const value = row[index];
    if (!_.isNil(value)) {
      return value;
    }
  }
  return undefined;
};

/* Filter out property columns and uom columns from headers and rows so we do not put extra data in the store */
export const filterPropertyAndUomColumns = (
  headers: MaterializedTableHeader[],
  processedRows: ProcessedTableCell[][],
  hasMore: boolean,
) => {
  const propertyAndUomColumnIndices = headers
    .map((header, index) => ({ name: getHeaderName(header), index }))
    .filter(
      ({ name }) =>
        name.includes(PROPERTY_COLUMN_MATCH_SEGMENT) ||
        name.includes(UOM_COLUMN_MATCH_SUFFIX) ||
        name.includes(SeeqNames.MaterializedTables.MaterializedTableJoinSuffix),
    )
    .map(({ index }) => index);
  const filteredHeaders = headers.filter((_, index) => !propertyAndUomColumnIndices.includes(index));
  const filteredRows = processedRows.map((row) =>
    row.filter((_, index) => !propertyAndUomColumnIndices.includes(index)),
  );

  return {
    headers: filteredHeaders,
    rows: filteredRows,
    hasMore,
  };
};

const overrideIndicator = (row: MaterializedTableRow, overrideIndex: number | undefined) => {
  if (overrideIndex === undefined) {
    return false;
  }
  const overrideValue = row[overrideIndex];
  return overrideValue !== undefined && overrideValue !== null;
};

const evaluationRuleResults = (
  row: MaterializedTableRow,
  itemIdIndex: number,
  datumIdIndex: number,
  evaluationLookup: EvaluationLookup | undefined,
) => {
  if (itemIdIndex < 0) {
    return undefined;
  }
  if (!evaluationLookup) {
    return undefined;
  }

  const itemIdValue = row[itemIdIndex];
  if (_.isNil(itemIdValue)) {
    return undefined;
  }
  const itemIdKey = itemIdValue.toString().toUpperCase();
  const datumIdKey =
    datumIdIndex >= 0 && !_.isNil(row[datumIdIndex]) ? row[datumIdIndex]?.toString().toUpperCase() : '';
  return (
    evaluationLookup.byItemAndDatum.get(`${itemIdKey}|${datumIdKey}`) ?? evaluationLookup.byItemFirst.get(itemIdKey)
  );
};

const processDefaultCell = (cellValue: MaterializedTableCell) => {
  return {
    value: cellValue ? cellValue.toString() : undefined,
  } as TextTableCell;
};

/** Process numeric values with optional UOM lookup. */
const processNumericCell = ({
  cellValue,
  row,
  uomIndex,
  column,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  uomIndex: number | undefined;
  column: ColumnDefinition | undefined;
}) => {
  const potentialNumericValue = toNumber(cellValue);
  const potentialUomColumnValue = uomIndex !== undefined ? row[uomIndex] : undefined;
  // If there was not a uom column, then check the column definition for a uom override
  const uomColumnValue = potentialUomColumnValue ?? column?.columnUom;

  if (_.isNil(potentialNumericValue)) {
    return processDefaultCell(cellValue);
  } else {
    return {
      value: potentialNumericValue,
      uom: uomColumnValue as string | undefined,
    } as Partial<NumericTableCell>;
  }
};

type MetadataTableColumnHeaders = {
  metadataName: string;
  columnName: string;
  sourceTableId: string;
  header: MaterializedTableHeader;
};

const UUID_REGEX = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
/** Parse materialized-table join headers into metadata descriptors. */
const extractMetadataHeaders = (potentialMetadataHeaders: MaterializedTableHeader[]) => {
  const metadataTableColumns: MetadataTableColumnHeaders[] = potentialMetadataHeaders.map((header) => {
    // Example of generated metadata column:
    // "Reactor Temperature Alias.0f0df64e-12a6-7120-8a8e-b00504d7f6b6_mt_join.Alias Column"
    // so [columnName].[sourceTableId]._mt_join.[metadataName] but metadataName can have periods in it and so can columnName
    const headerName = getHeaderName(header);
    const segments = headerName.split(SeeqNames.MaterializedTables.MaterializedTableJoinSuffix);
    const preJoinSegment = segments[0]; // "Reactor Temperature Alias.0f0df64e-12a6-7120-8a8e-b00504d7f6b6"
    const postJoinSegment = segments.length > 1 ? segments[1] : ''; // ".Alias Column" or ""
    const sourceTableIdMatch = preJoinSegment.match(UUID_REGEX);
    const sourceTableId = sourceTableIdMatch ? sourceTableIdMatch[0] : '';
    const columnName = preJoinSegment.replace(`.${sourceTableId}`, '');
    const metadataName = postJoinSegment.startsWith('.') ? postJoinSegment.slice(1) : postJoinSegment; // "Alias Column"

    return {
      metadataName,
      columnName,
      sourceTableId: sourceTableId.toUpperCase(),
      header,
    };
  });
  return metadataTableColumns;
};

/** Process UUID cells, attaching properties and alias metadata when present. */
const processUuidCell = ({
  cellValue,
  row,
  propertyIndices,
  aliasHeaderIndices,
}: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  propertyIndices: PropertyHeaderIndex[];
  aliasHeaderIndices?: number[];
}) => {
  const potentialAliasValue = getFirstDefinedValue(row, aliasHeaderIndices);

  const fetchedProperties = propertyIndices.reduce(
    (props, { property, index }) => {
      props[property] = index !== undefined ? (row[index] as string) : undefined;
      return props;
    },
    {} as Record<string, string | undefined>,
  );

  // If the value is the same as the table definition id, then it is a not found item from CSV import
  const id =
    cellValue === sqScalingTableDefinitionStore.id
      ? { tableDefinitionId: cellValue as string }
      : { uuid: cellValue as string };

  return {
    ...id,
    fetchedProperties,
    isModifiedOutsideTable: fetchedProperties[SeeqNames.Properties.IsModifiedOutsideTable],
    joinedMetadata: potentialAliasValue ? { alias: potentialAliasValue as string } : undefined,
  } as Partial<ItemTableCell>;
};

/** Process boolean cells with a consistent truthy conversion. */
const processBooleanCell = (cellValue: MaterializedTableCell) => {
  return {
    value: !!cellValue,
  } as Partial<BooleanTableCell>;
};

/** Process a cell based on its header type. */
const processCellByType = (processCellProps: {
  cellValue: MaterializedTableCell;
  row: MaterializedTableRow;
  plan: HeaderProcessingPlan;
}) => {
  if (_.isNil(processCellProps.cellValue)) {
    return {
      value: undefined,
    };
  }

  switch (processCellProps.plan.header.type) {
    case ColumnTypeEnum.NUMERIC:
      return processNumericCell({
        cellValue: processCellProps.cellValue,
        row: processCellProps.row,
        uomIndex: processCellProps.plan.uomIndex,
        column: processCellProps.plan.column,
      });
    case ColumnTypeEnum.UUID:
      return processUuidCell({
        cellValue: processCellProps.cellValue,
        row: processCellProps.row,
        propertyIndices: processCellProps.plan.propertyIndices ?? [],
        aliasHeaderIndices: processCellProps.plan.aliasHeaderIndices,
      });
    case ColumnTypeEnum.BOOLEAN:
      return processBooleanCell(processCellProps.cellValue);
    default: {
      return processDefaultCell(processCellProps.cellValue);
    }
  }
};

/** Convert a single materialized row into processed cells using shared lookups. */
const mapRowToProcessedCells = (row: MaterializedTableRow, context: ProcessingContext): ProcessedTableCell[] => {
  const processedRow: ProcessedTableCell[] = new Array(context.processingPlans.length);
  for (let columnIndex = 0; columnIndex < context.processingPlans.length; columnIndex += 1) {
    const plan = context.processingPlans[columnIndex];
    const cellValue = row[columnIndex];
    processedRow[columnIndex] = {
      override: overrideIndicator(row, plan.overrideIndex),
      evaluationDetails: evaluationRuleResults(row, context.itemIdIndex, context.datumIdIndex, plan.evaluationLookup),
      ...processCellByType({
        cellValue,
        row,
        plan,
      }),
    };
  }
  return processedRow;
};

/** Build the shared lookup context required to process rows. */
const buildProcessingContext = (
  headers: MaterializedTableHeader[],
  columns: ColumnDefinition[],
  tableJoinData?: TableJoinData[],
): ProcessingContext => {
  const headerIndexByName = buildHeaderIndexMap(headers);
  const columnsByName = buildColumnByNameMap(columns);
  const overrideIndexByColumnId = buildOverrideIndexMap(headers);
  const propertyIndexByColumnName = buildPropertyIndexMap(columns, headerIndexByName);
  const aliasHeaderIndexByColumnName = buildAliasHeaderIndexMap(headers, headerIndexByName, tableJoinData);
  const evaluationLookupByColumnName = buildEvaluationLookupMap(columns);
  const itemIdIndex = headerIndexByName.get(SeeqNames.MaterializedTables.ItemIdColumn) ?? -1;
  const datumIdIndex = headerIndexByName.get(SeeqNames.MaterializedTables.DatumIdColumn) ?? -1;
  const processingPlans = buildHeaderProcessingPlans(
    headers,
    columnsByName,
    overrideIndexByColumnId,
    headerIndexByName,
    propertyIndexByColumnName,
    aliasHeaderIndexByColumnName,
    evaluationLookupByColumnName,
  );

  return {
    processingPlans,
    itemIdIndex,
    datumIdIndex,
  };
};

/** Process a raw materialized table and its joined metadata into cells that contain all information necessary to
 * display them in aggrid
 */
export const processMaterializedTable = (
  rawMaterializedTable: MaterializedTableOutput | undefined,
  columns: ColumnDefinition[],
  tableJoinData?: TableJoinData[],
): ProcessedMaterializedTable | undefined => {
  if (!rawMaterializedTable) {
    return undefined;
  }
  const rows = rawMaterializedTable.rows;
  const headers = rawMaterializedTable.headers;
  const processingContext = buildProcessingContext(headers, columns, tableJoinData);

  const processedRows: ProcessedTableCell[][] = rows.map((row) => mapRowToProcessedCells(row, processingContext));

  return filterPropertyAndUomColumns(headers, processedRows, rawMaterializedTable.hasMore);
};

export const getRowInTableByItemId = (itemIdColumnValue: string, editorType: EditorType) => {
  const tableDefinitionStore = getTableDefinitionStore(editorType);
  const materializedTable = tableDefinitionStore.materializedTable;
  const tableType = tableDefinitionStore.tableType;
  const columnHeaderToSearch = CSV_TYPES.includes(tableType)
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  const headerIndex = materializedTable?.headers.findIndex((header) => getHeaderName(header) === columnHeaderToSearch);
  if (headerIndex === undefined || headerIndex < 0) {
    return undefined;
  }

  return materializedTable?.rows?.find((row) => {
    const itemIdColumnCellToCheck = row[headerIndex] as ItemTableCell;
    return itemIdColumnCellToCheck && itemIdColumnCellToCheck.uuid === itemIdColumnValue;
  });
};
